import { useState } from "react";
import { useSelector } from "react-redux";
import { Disclosure } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import {
  isAllowedToMakeCCPayment,
  isOldAlicUlPolicy,
  isPolicyInactiveByPolicyStatus,
} from "js/utilities";

import config from "config/config";
import Toast from "components/General/Toast";
import PanelOne from "components/Payments/MakeAPayment/PanelOne";
import PanelTwo from "components/Payments/MakeAPayment/PanelTwo";
import PanelThree from "components/Payments/MakeAPayment/PanelThree";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import AuthorizePaymentNameValidationModal from "components/ModalDialogs/AuthorizePaymentNameValidationModal";

const MakeAPayment = () => {
  let navigate = useNavigate();
  const { policies } = useSelector((state) => state.data);
  const { user } = useSelector((s) => s.user);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [amount, setAmount] = useState();
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentType, setPaymentType] = useState();
  const [selectedPolicy, setSelectedPolicy] = useState();
  const [loan, setLoan] = useState();
  const [isDisabled, setIsDisabled] = useState(true);
  const [isStepTwoChecked, setStepTwoChecked] = useState(false);
  const cancel = () => {
    navigate("/policies", { replace: true });
  };

  const [
    creditCardValidationCheckModalState,
    setCreditCardValidationCheckModalState,
  ] = useState(false);
  const [toastDetail, setToastDetail] = useState({
    title: "title",
    message: "message",
    type: undefined,
  });
  const isOldAlicUl = isOldAlicUlPolicy(selectedPolicy);
  const isNonNaturalEntity = Boolean(
    selectedPolicy?.parties?.find(
      (p) =>
        p?.person && p.person.partyTypeCode === "OW" && p.person.taxType === "1"
    )
  );

  useBreadcrumb([
    { name: t("CustomerManagePayment-home"), url: "/policies" },
    { name: t("CustomerManagePayment-payments"), url: "/payment" },
    { name: t("MakePayment-title"), url: "#" },
  ]);

  return (
    <>
      <EverlakeLoadingOverlay isLoading={isLoading}>
        <div className="flex flex-col flex-grow">
          <div className="border border-transparent">
            <div className="container mb-8">
              <h1>{t("MakePayment-title")}</h1>
              {config.COMPANY_NAME === "everlake" && (
                <Trans defaults={t("MakePayment-desc")} />
              )}
              <hr />
            </div>

            {toastDetail.type && (
              <div className="ui-component-spacing">
                <Toast {...toastDetail} />
              </div>
            )}

            <div className="container mb-8">
              <div className="ui-form-make-payment container">
                {/* <!-- Step 1 --> */}
                <div
                  id="ID_paymentStep1"
                  className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16"
                >
                  <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
                    <div className="flex justify-center items-center mt-6 mb-4">
                      <div
                        id="ID_paymentStep1Number"
                        className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                          currentStep >= 1
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        } rounded-full overflow-hidden`}
                      >
                        {t("CustomerMakeAPayment-step-one")}
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>

                  <div className="w-full flex-grow">
                    {/* <!-- Step 1 Card --> */}

                    <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                      <Disclosure as="div">
                        {({ open }) => (
                          <>
                            <div>
                              <div className="flex justify-between items-start">
                                <h2 className="flex flex-grow font-body font-bold text-xl">
                                  {t("MakePayment-stepTitle5")}
                                </h2>
                                <div className="font-semibold text-sm whitespace-nowrap">
                                  {t("MakePayment-step1")}
                                </div>
                              </div>
                            </div>

                            <div
                              id="ID_paymentStep1Options"
                              className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-light"
                            >
                              <Disclosure.Panel
                                static
                                className="pb-2 text-sm text-gray-500"
                              >
                                <PanelOne
                                  dataArray={policies?.filter(
                                    (p) =>
                                      isAllowedToMakeCCPayment(
                                        p,
                                        "onetimepayment"
                                      ) &&
                                      !isPolicyInactiveByPolicyStatus(p?.status)
                                  )}
                                  setCurrentStep={setCurrentStep}
                                  selectedPolicy={selectedPolicy}
                                  setSelectedPolicy={setSelectedPolicy}
                                  setLoan={setLoan}
                                  user={user}
                                  setIsLoading={setIsLoading}
                                />
                              </Disclosure.Panel>
                            </div>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  </div>
                </div>

                {/* <!-- Step 2 --> */}
                <div
                  id="ID_paymentStep2"
                  className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16"
                >
                  <div
                    className={`w-8 md:flex md:flex-col ${
                      isOldAlicUl ? "" : "md:justify-center"
                    } md:mr-14`}
                  >
                    <div className="flex justify-center items-center mt-3 mb-4">
                      <div
                        id="ID_paymentStep2Number"
                        className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                          currentStep >= 2
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        } rounded-full overflow-hidden`}
                      >
                        {t("CustomerMakeAPayment-step-two")}
                      </div>
                    </div>
                    {!isOldAlicUl && (
                      <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                    )}
                  </div>
                  {/* <!-- Card 2 --> */}

                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                      <Disclosure as="div">
                        {({ open }) => (
                          <>
                            <div>
                              <div className="flex justify-between items-start">
                                <h2 className="flex flex-grow font-body font-bold text-xl">
                                  {isOldAlicUl
                                    ? t(
                                        "CustomerMakeAPayment-step-two-oldAlicUl-title1"
                                      )
                                    : t("CustomerMakeAPayment-step-two-title", {
                                        value: loan ? " or Loan" : "",
                                      })}
                                </h2>
                                <div className="font-semibold text-sm whitespace-nowrap">
                                  {t("MakePayment-step2")}
                                </div>
                              </div>
                            </div>
                            <div
                              id="ID_paymentStep2Options"
                              className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-light"
                            >
                              {currentStep > 1 && (
                                <Disclosure.Panel
                                  className="pb-2 text-sm text-gray-500"
                                  static
                                >
                                  <PanelTwo
                                    setCurrentStep={setCurrentStep}
                                    selectedPolicy={selectedPolicy}
                                    loan={loan}
                                    setPaymentType={setPaymentType}
                                    setIsDisabled={setIsDisabled}
                                    setAmount={setAmount}
                                    isStepTwoChecked={isStepTwoChecked}
                                    setStepTwoChecked={setStepTwoChecked}
                                  />
                                </Disclosure.Panel>
                              )}
                            </div>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  </div>
                </div>

                {/* <!-- Step 3 --> */}
                {!isOldAlicUl && (
                  <div
                    id="ID_paymentStep3"
                    className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16"
                  >
                    <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
                      <div className="flex justify-center items-center mt-3 mb-4">
                        <div
                          id="ID_paymentStep3Number"
                          className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                            currentStep >= 3
                              ? "bg-ui-blue-dark"
                              : "bg-ui-gray-medium-dark"
                          } rounded-full overflow-hidden`}
                        >
                          {t("CustomerMakeAPayment-step-three")}
                        </div>
                      </div>
                      <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                    </div>

                    {/* Card 3 */}
                    <div className="w-full flex-grow">
                      <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                        <Disclosure as="div">
                          {({ open }) => (
                            <>
                              <div>
                                <div className="flex justify-between items-start">
                                  <h2 className="flex flex-grow font-body font-bold text-xl">
                                    {t("MakePayment-stepTitle7")}
                                  </h2>
                                  <div className="font-semibold text-sm whitespace-nowrap">
                                    {t("MakePayment-step3")}
                                  </div>
                                </div>
                              </div>
                              <div
                                id="ID_paymentStep3Options"
                                className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-light"
                              >
                                {currentStep > 2 && (
                                  <Disclosure.Panel
                                    className="pb-2 text-sm text-gray-500 items-center"
                                    static
                                  >
                                    <PanelThree
                                      setIsDisabled={setIsDisabled}
                                      paymentType={paymentType}
                                      selectedPolicy={selectedPolicy}
                                      setAmount={setAmount}
                                      loan={loan}
                                    />
                                  </Disclosure.Panel>
                                )}
                              </div>
                            </>
                          )}
                        </Disclosure>
                      </div>
                    </div>
                  </div>
                )}

                <div className="block text-right mb-8 md:ml-12 md:mr-6 lg:ml-28 lg:pr-16">
                  <button
                    className="ui-btn-secondary min-w-[140px]"
                    aria-label="cancel make a payment"
                    onClick={() => cancel()}
                  >
                    {t("MakePayment-button1")}
                  </button>
                  <button
                    id="ID_submitPayment"
                    className="ui-btn-primary min-w-[140px]"
                    aria-label="submit payment"
                    disabled={isDisabled || (isOldAlicUl && !isStepTwoChecked)}
                    onClick={() => setCreditCardValidationCheckModalState(true)}
                  >
                    {t("MakePayment-button2")}
                  </button>
                </div>
                {/* Modal Dialogs */}
                <AuthorizePaymentNameValidationModal
                  modalState={creditCardValidationCheckModalState}
                  setModalState={setCreditCardValidationCheckModalState}
                  selectedPolicy={selectedPolicy}
                  amount={
                    isOldAlicUl
                      ? amount
                      : paymentType === "premiumpayment"
                      ? selectedPolicy.modalPremiumAmountTotal
                      : amount
                  }
                  setToastDetail={setToastDetail}
                  isNonNaturalEntity={isNonNaturalEntity}
                />
              </div>
            </div>
          </div>
        </div>
      </EverlakeLoadingOverlay>
    </>
  );
};

export default MakeAPayment;
